import { useState, useEffect } from 'react'
import { useMapSettings, useSaveMapSettings } from '../../api/settings'
import { useToast } from '../../hooks/useToast'
import type { MapSettings } from '../../api/settings'
import type { WmsSourceConfig } from '../../store/mapStore'

const COORD_FORMATS: { value: MapSettings['preferredCoordFormat']; label: string }[] = [
  { value: 'dd', label: 'Decimal Degrees' },
  { value: 'dms', label: 'DMS' },
  { value: 'mgrs', label: 'MGRS' },
  { value: 'utm', label: 'UTM' },
]

interface WmsSourceDraft {
  id: string
  name: string
  endpoint: string
  layersText: string
}

function generateSourceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `wms-${Date.now()}-${Math.floor(Math.random() * 1000)}`
}

function normalizeSourceDrafts(drafts: WmsSourceDraft[]): WmsSourceConfig[] {
  return drafts
    .map((draft) => ({
      id: draft.id.trim(),
      name: draft.name.trim() || 'WMS Source',
      endpoint: draft.endpoint.trim(),
      layers: draft.layersText
        .split(',')
        .map((layer) => layer.trim())
        .filter(Boolean),
    }))
    .filter((source) => source.id && source.endpoint)
}

export default function MapSettingsTab() {
  const { data: settings, isLoading } = useMapSettings()
  const { mutate: save, isPending } = useSaveMapSettings()
  const { toast } = useToast()

  const [wmsSources, setWmsSources] = useState<WmsSourceDraft[]>([])
  const [activeWmsSourceId, setActiveWmsSourceId] = useState<string | null>(null)
  const [centerLon, setCenterLon] = useState('0')
  const [centerLat, setCenterLat] = useState('20')
  const [zoom, setZoom] = useState('3')
  const [coordFormat, setCoordFormat] = useState<MapSettings['preferredCoordFormat']>('dd')

  useEffect(() => {
    if (!settings) return
    const seedSources =
      settings.wmsSources.length > 0
        ? settings.wmsSources
        : settings.wmsEndpoint
          ? [{
              id: settings.activeWmsSourceId ?? 'legacy-default',
              name: 'Default WMS',
              endpoint: settings.wmsEndpoint,
              layers: settings.wmsLayers,
            }]
          : []

    setWmsSources(
      seedSources.map((source) => ({
        id: source.id,
        name: source.name,
        endpoint: source.endpoint,
        layersText: source.layers.join(', '),
      }))
    )
    setActiveWmsSourceId(settings.activeWmsSourceId ?? seedSources[0]?.id ?? null)
    setCenterLon(String(settings.defaultCenterLon))
    setCenterLat(String(settings.defaultCenterLat))
    setZoom(String(settings.defaultZoom))
    setCoordFormat(settings.preferredCoordFormat)
  }, [settings])

  function addSource() {
    const next: WmsSourceDraft = {
      id: generateSourceId(),
      name: `Source ${wmsSources.length + 1}`,
      endpoint: '',
      layersText: '',
    }
    setWmsSources((prev) => [...prev, next])
    if (!activeWmsSourceId) setActiveWmsSourceId(next.id)
  }

  function updateSource(id: string, patch: Partial<WmsSourceDraft>) {
    setWmsSources((prev) =>
      prev.map((source) => (source.id === id ? { ...source, ...patch } : source))
    )
  }

  function removeSource(id: string) {
    setWmsSources((prev) => {
      const next = prev.filter((source) => source.id !== id)
      if (activeWmsSourceId === id) {
        setActiveWmsSourceId(next[0]?.id ?? null)
      }
      return next
    })
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    const normalizedSources = normalizeSourceDrafts(wmsSources)
    const activeSource =
      normalizedSources.find((source) => source.id === activeWmsSourceId) ??
      normalizedSources[0] ??
      null

    save(
      {
        wmsSources: normalizedSources,
        activeWmsSourceId: activeSource?.id ?? null,
        wmsEndpoint: activeSource?.endpoint ?? '',
        wmsLayers: activeSource?.layers ?? [],
        defaultCenterLon: parseFloat(centerLon),
        defaultCenterLat: parseFloat(centerLat),
        defaultZoom: parseInt(zoom),
        preferredCoordFormat: coordFormat,
      },
      {
        onSuccess: () => toast({ title: 'Map settings saved' }),
        onError: () => toast({ title: 'Failed to save', variant: 'destructive' }),
      }
    )
  }

  if (isLoading) return <div className="animate-pulse h-48 rounded-lg bg-muted" />

  return (
    <form onSubmit={handleSave} className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">WMS Sources</h2>
          <button
            type="button"
            onClick={addSource}
            className="rounded-md border border-border px-3 py-1 text-xs hover:bg-accent/50"
          >
            + Add Source
          </button>
        </div>

        {wmsSources.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No saved sources yet. Add one endpoint/layer pair and select the default source.
          </p>
        )}

        {wmsSources.map((source) => (
          <div key={source.id} className="rounded-lg border border-border p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <label className="text-xs font-medium flex items-center gap-2">
                <input
                  type="radio"
                  name="active-wms-source"
                  checked={activeWmsSourceId === source.id}
                  onChange={() => setActiveWmsSourceId(source.id)}
                />
                Default On Login
              </label>
              <button
                type="button"
                onClick={() => removeSource(source.id)}
                className="text-xs text-destructive hover:underline"
              >
                Remove
              </button>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Source Name</label>
              <input
                type="text"
                value={source.name}
                onChange={(e) => updateSource(source.id, { name: e.target.value })}
                placeholder="USGS Topo"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">WMS Endpoint URL</label>
              <input
                type="url"
                value={source.endpoint}
                onChange={(e) => updateSource(source.id, { endpoint: e.target.value })}
                placeholder="http://geoserver.local:8080/geoserver/wms"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Layer Names</label>
              <input
                type="text"
                value={source.layersText}
                onChange={(e) => updateSource(source.id, { layersText: e.target.value })}
                placeholder="layer1, layer2, layer3"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Default View</h2>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Default Longitude</label>
            <input
              type="number"
              step="any"
              value={centerLon}
              onChange={(e) => setCenterLon(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Default Latitude</label>
            <input
              type="number"
              step="any"
              value={centerLat}
              onChange={(e) => setCenterLat(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Default Zoom</label>
            <input
              type="number"
              min="1"
              max="20"
              value={zoom}
              onChange={(e) => setZoom(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Coordinate Format</h2>
        <div className="flex flex-wrap gap-2">
          {COORD_FORMATS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setCoordFormat(value)}
              className={`rounded-md px-4 py-2 text-sm font-medium border transition-colors
                ${coordFormat === value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/50'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Highlighted format shown in the coordinate bar. All formats are always displayed.
        </p>
      </div>

      <button
        type="submit"
        disabled={isPending}
        className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {isPending ? 'Saving...' : 'Save Settings'}
      </button>
    </form>
  )
}
